import React, { useState, useEffect } from 'react';
import { 
  FolderKanban, Plus, Search, Filter, Clock, User, Calendar,
  MoreVertical, Edit3, Trash2, X, Loader2, CheckCircle2, Circle,
  AlertCircle, ChevronRight, Target, Briefcase, Palette, Heart
} from 'lucide-react';
import { useUserEmail } from '../../context/UserAuthContext';

const API = typeof window !== 'undefined' ? window.location.origin : '';

const PROJECT_DIVISIONS = {
  personal: { label: 'Personal Brand', icon: User, color: 'bg-blue-500/20 text-blue-400' },
  performance: { label: 'Performance', icon: Target, color: 'bg-green-500/20 text-green-400' },
  impact: { label: 'Impact/Foundation', icon: Heart, color: 'bg-pink-500/20 text-pink-400' },
  studio: { label: 'Studio/Creative', icon: Palette, color: 'bg-purple-500/20 text-purple-400' }
};

const PROJECT_STATUS = {
  planning: { label: 'Planning', color: 'bg-gray-500/20 text-gray-400' },
  active: { label: 'Active', color: 'bg-blue-500/20 text-blue-400' },
  on_hold: { label: 'On Hold', color: 'bg-yellow-500/20 text-yellow-400' },
  completed: { label: 'Completed', color: 'bg-green-500/20 text-green-400' }
};

const PRIORITY_COLORS = {
  high: 'bg-red-500/20 text-red-400',
  medium: 'bg-yellow-500/20 text-yellow-400',
  low: 'bg-green-500/20 text-green-400'
};

const AEProjects = () => {
  const userEmail = useUserEmail();
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterDivision, setFilterDivision] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedProject, setSelectedProject] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    division: 'personal',
    priority: 'medium',
    status: 'planning',
    start_date: '',
    deadline: '',
    pillar: '',
    team_members: ''
  });

  useEffect(() => {
    if (userEmail) {
      checkRole();
      fetchProjects();
    }
  }, [userEmail]);

  const checkRole = async () => {
    try {
      const res = await fetch(`${API}/api/always-elevate/user-role?user_email=${encodeURIComponent(userEmail)}`);
      const data = await res.json();
      setIsAdmin(data.role === 'admin');
    } catch (err) {
      console.error('Error checking role:', err);
    }
  };

  const fetchProjects = async () => {
    try {
      const res = await fetch(`${API}/api/always-elevate/projects?user_email=${encodeURIComponent(userEmail)}`);
      const data = await res.json();
      if (data.success) {
        setProjects(data.data);
      }
    } catch (err) {
      console.error('Error fetching projects:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.title) return;
    setSaving(true);
    
    try {
      const payload = {
        ...formData,
        team_members: formData.team_members.split(',').map(t => t.trim()).filter(Boolean)
      };
      
      const res = await fetch(`${API}/api/always-elevate/projects?user_email=${encodeURIComponent(userEmail)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (data.success) {
        setShowCreateModal(false);
        resetForm();
        fetchProjects();
      }
    } catch (err) {
      console.error('Error creating project:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateStatus = async (projectId, newStatus) => {
    try {
      const res = await fetch(`${API}/api/always-elevate/projects/${projectId}?user_email=${encodeURIComponent(userEmail)}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });
      const data = await res.json();
      if (data.success) {
        fetchProjects();
        if (selectedProject?.id === projectId) {
          setSelectedProject({ ...selectedProject, status: newStatus });
        }
      }
    } catch (err) {
      console.error('Error updating project:', err);
    }
  };

  const handleDelete = async (projectId) => {
    if (!window.confirm('Are you sure you want to delete this project?')) return;
    
    try {
      const res = await fetch(`${API}/api/always-elevate/projects/${projectId}?user_email=${encodeURIComponent(userEmail)}`, {
        method: 'DELETE'
      });
      const data = await res.json();
      if (data.success) {
        fetchProjects();
        setSelectedProject(null);
      }
    } catch (err) {
      console.error('Error deleting project:', err);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      division: 'personal',
      priority: 'medium',
      status: 'planning',
      start_date: '',
      deadline: '',
      pillar: '',
      team_members: ''
    });
  };

  const filteredProjects = projects.filter(project => {
    if (searchQuery && !project.title.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }
    if (filterDivision !== 'all' && project.division !== filterDivision) {
      return false;
    }
    if (filterStatus !== 'all' && project.status !== filterStatus) {
      return false;
    }
    return true;
  });

  const stats = {
    total: projects.length,
    active: projects.filter(p => p.status === 'active').length,
    completed: projects.filter(p => p.status === 'completed').length,
    planning: projects.filter(p => p.status === 'planning').length
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#C4A052]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white" data-testid="ae-projects-page">
      {/* Header */}
      <header className="border-b border-white/10 px-6 py-6">
        <div className="max-w-[1800px] mx-auto">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[#C4A052] text-sm font-medium tracking-wider">ALWAYS ELEVATE</p>
              <h1 className="text-3xl font-bold mt-1" style={{ fontFamily: 'Anton, sans-serif' }} data-testid="ae-projects-title">
                Projects
              </h1>
              <p className="text-gray-400 text-sm mt-1">Manage brand projects and initiatives</p>
            </div>
            {isAdmin && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-[#C4A052] text-black rounded-lg font-medium hover:bg-[#D4B062] transition-colors"
                data-testid="create-project-btn"
              >
                <Plus className="w-4 h-4" />
                New Project
              </button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-[1800px] mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-4 gap-4 mb-8">
          <div className="bg-black border border-white/20 rounded-xl p-4 transition-all hover:border-blue-500/50 hover:scale-[1.02]">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500/20 rounded-lg">
                <FolderKanban className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.total}</p>
                <p className="text-xs text-gray-400">Total Projects</p>
              </div>
            </div>
          </div>
          <div className="bg-black border border-white/20 rounded-xl p-4 transition-all hover:border-green-500/50 hover:scale-[1.02]">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-500/20 rounded-lg">
                <CheckCircle2 className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.active}</p>
                <p className="text-xs text-gray-400">Active</p>
              </div>
            </div>
          </div>
          <div className="bg-black border border-white/20 rounded-xl p-4 transition-all hover:border-purple-500/50 hover:scale-[1.02]">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-500/20 rounded-lg">
                <Circle className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.planning}</p>
                <p className="text-xs text-gray-400">Planning</p>
              </div>
            </div>
          </div>
          <div className="bg-black border border-white/20 rounded-xl p-4 transition-all hover:border-[#C4A052]/50 hover:scale-[1.02]">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-[#C4A052]/20 rounded-lg">
                <CheckCircle2 className="w-5 h-5 text-[#C4A052]" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.completed}</p>
                <p className="text-xs text-gray-400">Completed</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-4 mb-6">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input
              type="text"
              placeholder="Search projects..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-black border border-white/20 rounded-lg pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:border-[#C4A052]"
              data-testid="project-search"
            />
          </div>
          
          <select
            value={filterDivision}
            onChange={(e) => setFilterDivision(e.target.value)}
            className="bg-black border border-white/20 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#C4A052]"
          >
            <option value="all">All Divisions</option>
            {Object.entries(PROJECT_DIVISIONS).map(([key, val]) => (
              <option key={key} value={key}>{val.label}</option>
            ))}
          </select>
          
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="bg-black border border-white/20 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#C4A052]"
          >
            <option value="all">All Status</option>
            {Object.entries(PROJECT_STATUS).map(([key, val]) => (
              <option key={key} value={key}>{val.label}</option>
            ))}
          </select>
        </div>

        {/* Projects Grid */}
        {filteredProjects.length === 0 ? (
          <div className="bg-black border border-white/20 rounded-xl p-12 text-center">
            <FolderKanban className="w-16 h-16 mx-auto mb-4 text-gray-600" />
            <h3 className="text-lg font-medium text-gray-400 mb-2">No Projects Found</h3>
            <p className="text-gray-500 text-sm mb-4">
              {searchQuery || filterDivision !== 'all' || filterStatus !== 'all'
                ? 'Try adjusting your filters'
                : 'Create your first project to get started'}
            </p>
            {isAdmin && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-[#C4A052] text-black rounded-lg font-medium hover:bg-[#D4B062] transition-colors"
              >
                <Plus className="w-4 h-4" />
                New Project
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="projects-grid">
            {filteredProjects.map(project => {
              const division = PROJECT_DIVISIONS[project.division] || PROJECT_DIVISIONS.personal;
              const status = PROJECT_STATUS[project.status] || PROJECT_STATUS.planning;
              const DivisionIcon = division.icon;
              
              return (
                <div
                  key={project.id}
                  onClick={() => setSelectedProject(project)}
                  className={`bg-white/5 border rounded-xl p-5 cursor-pointer ${
                    selectedProject?.id === project.id ? 'border-[#C4A052]' : 'border-white/10'
                  }`}
                  data-testid={`project-card-${project.id}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className={`p-2 rounded-lg ${division.color}`}>
                      <DivisionIcon className="w-4 h-4" />
                    </div>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${status.color}`}>
                      {status.label}
                    </span>
                  </div>
                  
                  <h3 className="font-semibold text-lg mb-2">{project.title}</h3>
                  
                  {project.description && (
                    <p className="text-sm text-gray-400 mb-3 line-clamp-2">{project.description}</p>
                  )}
                  
                  <div className="flex items-center gap-4 text-xs text-gray-500">
                    <span className={`px-2 py-0.5 rounded ${PRIORITY_COLORS[project.priority] || PRIORITY_COLORS.medium}`}>
                      {project.priority}
                    </span>
                    {project.deadline && (
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {new Date(project.deadline).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      {/* Project Detail Modal */}
      {selectedProject && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-[#0f0f0f] border border-white/10 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-white/10 flex items-center justify-between">
              <h3 className="text-lg font-bold">{selectedProject.title}</h3>
              <button 
                onClick={() => setSelectedProject(null)} 
                className="p-2 hover:bg-white/10 rounded-lg"
                data-testid="close-project-modal"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6">
              {/* Status & Priority */}
              <div className="flex items-center gap-3 mb-6">
                <span className={`px-3 py-1.5 rounded-lg text-sm font-medium ${PROJECT_STATUS[selectedProject.status]?.color}`}>
                  {PROJECT_STATUS[selectedProject.status]?.label}
                </span>
                <span className={`px-3 py-1.5 rounded-lg text-sm font-medium ${PRIORITY_COLORS[selectedProject.priority]}`}>
                  {selectedProject.priority} priority
                </span>
                <span className={`px-3 py-1.5 rounded-lg text-sm font-medium ${PROJECT_DIVISIONS[selectedProject.division]?.color}`}>
                  {PROJECT_DIVISIONS[selectedProject.division]?.label}
                </span>
              </div>

              {/* Description */}
              {selectedProject.description && (
                <div className="mb-6">
                  <p className="text-xs text-gray-500 mb-1">Description</p>
                  <p className="text-gray-300">{selectedProject.description}</p>
                </div>
              )}

              {/* Dates */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                {selectedProject.start_date && (
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Start Date</p>
                    <p className="text-sm">{new Date(selectedProject.start_date).toLocaleDateString()}</p>
                  </div>
                )}
                {selectedProject.deadline && (
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Deadline</p>
                    <p className="text-sm">{new Date(selectedProject.deadline).toLocaleDateString()}</p>
                  </div>
                )}
              </div>

              {/* Team Members */}
              {selectedProject.team_members && selectedProject.team_members.length > 0 && (
                <div className="mb-6">
                  <p className="text-xs text-gray-500 mb-2">Team Members</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedProject.team_members.map((member, idx) => (
                      <span key={idx} className="px-2 py-1 bg-white/10 rounded text-xs">
                        {member}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Status Update */}
              {isAdmin && (
                <div className="mb-6">
                  <p className="text-xs text-gray-500 mb-2">Update Status</p>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(PROJECT_STATUS).map(([key, val]) => (
                      <button
                        key={key}
                        onClick={() => handleUpdateStatus(selectedProject.id, key)}
                        className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                          selectedProject.status === key
                            ? val.color
                            : 'bg-white/5 text-gray-400 hover:bg-white/10'
                        }`}
                      >
                        {val.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Metadata */}
              <div className="text-xs text-gray-500">
                Created {new Date(selectedProject.created_at).toLocaleDateString()}
              </div>
            </div>
            
            <div className="p-6 border-t border-white/10 flex justify-between">
              {isAdmin && (
                <button
                  onClick={() => handleDelete(selectedProject.id)}
                  className="flex items-center gap-2 px-4 py-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors"
                  data-testid="delete-project-btn"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete
                </button>
              )}
              <button
                onClick={() => setSelectedProject(null)}
                className="px-4 py-2 bg-white/10 rounded-lg hover:bg-white/20 transition-colors ml-auto"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-[#0f0f0f] border border-white/10 rounded-xl max-w-lg w-full">
            <div className="p-6 border-b border-white/10 flex items-center justify-between">
              <h3 className="text-lg font-bold">New Project</h3>
              <button onClick={() => { setShowCreateModal(false); resetForm(); }} className="p-2 hover:bg-white/10 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full bg-black border border-white/20 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#C4A052]"
                  placeholder="Project title"
                />
              </div>
              
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full bg-black border border-white/20 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#C4A052] resize-none h-20"
                  placeholder="Project description"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Division</label>
                  <select
                    value={formData.division}
                    onChange={(e) => setFormData(prev => ({ ...prev, division: e.target.value }))}
                    className="w-full bg-black border border-white/20 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#C4A052]"
                  >
                    {Object.entries(PROJECT_DIVISIONS).map(([key, val]) => (
                      <option key={key} value={key}>{val.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Priority</label>
                  <select
                    value={formData.priority}
                    onChange={(e) => setFormData(prev => ({ ...prev, priority: e.target.value }))}
                    className="w-full bg-black border border-white/20 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#C4A052]"
                  >
                    <option value="high">High</option>
                    <option value="medium">Medium</option>
                    <option value="low">Low</option>
                  </select>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Start Date</label>
                  <input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData(prev => ({ ...prev, start_date: e.target.value }))}
                    className="w-full bg-black border border-white/20 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#C4A052]"
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Deadline</label>
                  <input
                    type="date"
                    value={formData.deadline}
                    onChange={(e) => setFormData(prev => ({ ...prev, deadline: e.target.value }))}
                    className="w-full bg-black border border-white/20 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#C4A052]"
                  />
                </div>
              </div>
              
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Team Members (comma separated)</label>
                <input
                  type="text"
                  value={formData.team_members}
                  onChange={(e) => setFormData(prev => ({ ...prev, team_members: e.target.value }))}
                  className="w-full bg-black border border-white/20 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#C4A052]"
                  placeholder="e.g. Elle Maxwell, John Doe"
                />
              </div>
            </div>
            
            <div className="p-6 border-t border-white/10 flex justify-end gap-3">
              <button
                onClick={() => { setShowCreateModal(false); resetForm(); }}
                className="px-4 py-2 bg-white/10 rounded-lg hover:bg-white/20 transition-colors"
                disabled={saving}
              >
                Cancel
              </button>
              <button 
                onClick={handleCreate}
                disabled={!formData.title || saving}
                className="flex items-center gap-2 px-4 py-2 bg-[#C4A052] text-black rounded-lg font-medium hover:bg-[#D4B062] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                Create Project
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AEProjects;
